import React, { useState, useEffect, useCallback } from "react";
import {
  PlusCircle,
  Edit,
  Trash2,
  X,
  Package,
  Utensils,
} from "lucide-react";
import { Power } from "lucide-react";
import { applyCategoryDefaults } from "../utils/categoryDefaults";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";

const API_BASE = "/api";

const CategoryModal = ({
  category,
  onSave,
  onCancel,
  isExtraCategory = false,
  isBoutique = false,
  isDarkMode,
}) => {
  const [formData, setFormData] = useState(category);

  useEffect(() => {
    setFormData(category);
  }, [category]);

  if (!category) return null;

  const labelClasses = `text-xs uppercase tracking-[0.3em] ${
    isDarkMode ? "text-white/60" : "text-slate-500"
  }`;
  const fieldClasses = `w-full rounded-2xl border px-4 py-3 text-sm ${
    isDarkMode
      ? "bg-white/5 border-white/15 text-slate-100 placeholder-white/40"
      : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
  }`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const accentBg = isExtraCategory
    ? "from-emerald-400/90 to-teal-500/90"
    : "from-amber-400/90 to-orange-500/90";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={handleBackdropClick}
    >
      <div
        className={`w-full max-w-xl rounded-3xl border ${
          isDarkMode
            ? "bg-[#0c162c] text-slate-100 border-white/10 shadow-2xl shadow-black/40"
            : "bg-white text-slate-900 border-slate-200 shadow-2xl shadow-slate-200/70"
        }`}
      >
        <div
          className={`rounded-t-3xl p-6 bg-gradient-to-r ${accentBg} text-slate-950`}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-slate-900/70">
                {formData.id ? "Edition" : "Nouvelle catégorie"}
              </p>
              <h2 className="text-2xl font-semibold">
                {isExtraCategory
                  ? "Catégorie d'extras"
                  : isBoutique
                    ? "Catégorie produits"
                    : "Catégorie menu"}
              </h2>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full bg-white/20 p-2 text-slate-900 hover:bg-white/40 transition"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-2">
            <label className={labelClasses}>Nom</label>
            <input
              name="name"
              value={formData.name || ""}
              onChange={handleChange}
              className={fieldClasses}
              placeholder={
                isExtraCategory
                  ? "Ex : Garnitures chaudes"
                  : isBoutique
                    ? "Ex : Épicerie salée"
                    : "Ex : Spécialités du chef"
              }
              required
            />
          </div>
          <div className="space-y-2">
            <label className={labelClasses}>Description</label>
            <textarea
              name="description"
              value={formData.description || ""}
              onChange={handleChange}
              className={`${fieldClasses} min-h-[110px]`}
              placeholder="Ajoutez quelques détails pour vos équipes"
            />
          </div>
          {!isExtraCategory && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className={labelClasses}>Couleur</label>
                <div
                  className={`flex items-center gap-3 rounded-2xl border px-3 py-3 ${
                    isDarkMode
                      ? "bg-white/5 border-white/15"
                      : "bg-white border-slate-200"
                  }`}
                >
                  <input
                    type="color"
                    name="color"
                    value={formData.color || "#FFB800"}
                    onChange={handleChange}
                    className="h-10 w-10 rounded-lg border-none bg-transparent p-0"
                  />
                  <div>
                    <p className="text-sm font-semibold">
                      Palette personnalisée
                    </p>
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-white/60" : "text-slate-500"
                      }`}
                    >
                      Cette couleur sera utilisée sur les cartes du menu
                    </p>
                  </div>
                </div>
              </div>
              {!isBoutique && (
                <div className="space-y-2">
                  <label className={labelClasses}>Icône</label>
                  <input
                    name="icon"
                    value={formData.icon || ""}
                    onChange={handleChange}
                    className={fieldClasses}
                    placeholder="Emoji ou icône (ex : 🍣)"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              className={`rounded-2xl px-5 py-3 text-sm font-semibold ${
                isDarkMode
                  ? "bg-white/5 border border-white/10 text-slate-300 hover:border-white/40"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`rounded-2xl px-5 py-3 text-sm font-semibold text-slate-950 bg-gradient-to-r ${accentBg} hover:opacity-90 transition`}
            >
              Enregistrer
            </button>
          </div>
        </form>
        <div
          className={`rounded-b-3xl px-6 py-4 flex items-center gap-2 border-t ${
            isDarkMode
              ? "border-white/5 text-white/60"
              : "border-slate-100 text-slate-500"
          }`}
        >
          <p className="text-xs">
            {isExtraCategory
              ? "Les extras apparaissent automatiquement dans la fiche produit."
              : "Ces catégories sont synchronisées avec la caisse et le menu."}
          </p>
        </div>
      </div>
    </div>
  );
};

const BoutiqueCategoryIcon = ({ className = "h-6 w-6 text-amber-300" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16.5 9.4 7.55 4.24"></path>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4A2 2 0 0 0 13 22l7-4A2 2 0 0 0 21 16z"></path>
    <polyline points="3.29 7 12 12 20.71 7"></polyline>
    <line x1="12" x2="12" y1="22" y2="12"></line>
  </svg>
);

const MenuCategoriesTab = ({ isDarkMode, cardClasses, isBoutique = false }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [orderedList, setOrderedList] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/categories`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur ${response.status}: ${errorText}`);
      }
      const data = await response.json();
      const normalized = Array.isArray(data)
        ? data.map((category) => applyCategoryDefaults(category))
        : [];
      setCategories(normalized);
      setOrderedList(normalized);
    } catch (err) {
      console.error("Erreur lors du chargement des catégories:", err);
      setError(`Erreur lors du chargement des catégories: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSaveCategory = async (category) => {
    const url = category.id
      ? `${API_BASE}/categories/${category.id}`
      : `${API_BASE}/categories`;
    const method = category.id ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(category),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Échec de la sauvegarde");
      }
      await fetchCategories();
      setEditingCategory(null);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde:", err);
      alert(`Erreur lors de la sauvegarde: ${err.message}`);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      const isActive =
        category.is_active === true ||
        category.is_active === 1 ||
        category.is_active === "1";
      const payload = { ...category, is_active: isActive ? 0 : 1 };
      const response = await fetch(`${API_BASE}/categories/${category.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || "Impossible de mettre à jour la catégorie");
      }
      await fetchCategories();
    } catch (err) {
      console.error("Erreur toggle catégorie:", err);
      alert(err.message || "Erreur lors du changement d'état");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (
      window.confirm("Êtes-vous sûr de vouloir supprimer cette catégorie ?")
    ) {
      try {
        const response = await fetch(`${API_BASE}/categories/${categoryId}`, {
          method: "DELETE",
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || "Échec de la suppression");
        }
        await fetchCategories();
      } catch (err) {
        console.error("Erreur lors de la suppression:", err);
        alert(`Erreur lors de la suppression: ${err.message}`);
      }
    }
  };

  const handleAddNew = () => {
    setEditingCategory({
      name: "",
      description: "",
      color: "#FFB800",
      icon: "🍽️",
    });
  };

  const textMuted = isDarkMode ? "text-white/60" : "text-slate-500";
  const subtleAccentText = isDarkMode ? "text-white/50" : "text-slate-400";
  const dividerClasses = isDarkMode ? "border-white/5" : "border-slate-100";

  const handleDragStart = (index) => (event) => {
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (targetIndex) => (event) => {
    event.preventDefault();
    const fromIndex = Number(event.dataTransfer.getData("text/plain"));
    if (Number.isNaN(fromIndex) || fromIndex === targetIndex) return;
    setOrderedList((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const ids = orderedList.map((c) => c.id);
      const res = await fetch("/api/categories/reorder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Impossible d'enregistrer l'ordre");
      }
      await fetchCategories();
      setReorderMode(false);
    } catch (err) {
      console.error("Reorder error:", err);
      alert(err.message || "Erreur lors de l'enregistrement de l'ordre");
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div className="space-y-4">
      {editingCategory && (
        <CategoryModal
          category={editingCategory}
          onSave={handleSaveCategory}
          onCancel={() => setEditingCategory(null)}
          isExtraCategory={false}
          isBoutique={isBoutique}
          isDarkMode={isDarkMode}
        />
      )}

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400">
            {isBoutique ? "Catégories produits" : "Catégories menu"}
          </p>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold">
              {isBoutique ? "Organisation produits" : "Organisation du menu"}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  isDarkMode
                    ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                }`}
              >
                {(categories.filter((c) => c.is_active === 1).length)} actives
              </span>
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  isDarkMode
                    ? "bg-rose-500/10 text-rose-200 border border-rose-400/30"
                    : "bg-rose-50 text-rose-700 border border-rose-100"
                }`}
              >
                {(categories.filter((c) => c.is_active !== 1).length)} inactives
              </span>
            </div>
          </div>
          <p className={`text-sm ${textMuted}`}>
            {isBoutique
              ? "Structurez vos familles de produits pour la caisse."
              : "Définissez la hiérarchie de votre carte et synchronisez avec la caisse."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setReorderMode((prev) => !prev)}
            className={`rounded-2xl px-5 py-3 text-sm font-semibold flex items-center gap-2 ${
              reorderMode
                ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30"
                : "bg-white/10 border border-white/20 text-slate-900 bg-gradient-to-r from-slate-100 to-slate-50 shadow"
            }`}
          >
            {reorderMode ? "Terminer l'ordre" : "Modifier l'ordre"}
          </button>
          <button
            onClick={handleAddNew}
            className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-amber-500/30 flex items-center gap-2"
          >
            <PlusCircle size={18} />
            Ajouter une catégorie
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-amber-400 border-b-transparent" />
        </div>
      ) : error ? (
        <div
          className={`${cardClasses} rounded-3xl border border-rose-500/30 p-6 ${
            isDarkMode ? "text-rose-200" : "text-rose-600"
          }`}
        >
          <p className="font-semibold">{error}</p>
          <button
            onClick={fetchCategories}
            className={`mt-4 rounded-2xl px-4 py-2 text-sm font-semibold ${
              isDarkMode
                ? "bg-rose-500/80 text-white"
                : "bg-rose-500 text-white"
            }`}
          >
            Réessayer
          </button>
        </div>
      ) : categories.length === 0 ? (
        <div className={`${cardClasses} rounded-3xl p-10 text-center`}>
          <p className="text-lg font-semibold">Aucune catégorie enregistrée</p>
          <p className={`text-sm mt-2 ${textMuted}`}>
            {isBoutique
              ? "Commencez par créer une catégorie pour structurer vos produits."
              : "Commencez par créer une catégorie pour structurer votre menu."}
          </p>
        </div>
      ) : (
        reorderMode ? (
          <div className={`${cardClasses} rounded-3xl p-6 space-y-4`}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                Réordonner les catégories (caisse)
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setReorderMode(false);
                    setOrderedList(categories);
                  }}
                  className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/10 hover:border-white/30"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveOrder}
                  disabled={savingOrder}
                  className="rounded-2xl px-4 py-2 text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-60"
                >
                  {savingOrder ? "Enregistrement..." : "Enregistrer l'ordre"}
                </button>
              </div>
            </div>
            <p className={`text-sm ${textMuted}`}>
              Glissez-déposez les catégories pour définir l'ordre d'affichage dans la caisse.
            </p>
            <div className="space-y-2">
              {orderedList.map((category, idx) => (
                <div
                  key={category.id}
                  draggable
                  onDragStart={handleDragStart(idx)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop(idx)}
                  className={`flex items-center justify-between rounded-2xl px-4 py-3 border ${
                    isDarkMode
                      ? "border-white/10 bg-white/5"
                      : "border-slate-200 bg-white"
                  } cursor-move gap-3`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="h-10 w-10 rounded-2xl flex items-center justify-center text-xl"
                      style={{
                        background: `${category.color || "#fbbf24"}20`,
                        color: category.color || "#fbbf24",
                      }}
                    >
                      {isBoutique ? (
                        <BoutiqueCategoryIcon className="h-6 w-6 text-amber-300" />
                      ) : (
                        category.icon || "🍽️"
                      )}
                    </div>
                    <div>
                      <p className="text-xs opacity-60">Catégorie</p>
                      <p className="font-semibold">{category.name}</p>
                    </div>
                  </div>
                  <div className="text-xs opacity-60">#{idx + 1}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {categories.map((category) => (
              <div
                key={category.id}
                className={`${cardClasses} rounded-3xl p-5 space-y-4`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-12 w-12 rounded-2xl flex items-center justify-center"
                      style={{
                        background: `${category.color || "#fbbf24"}20`,
                        color: category.color || "#fbbf24",
                      }}
                    >
                      {isBoutique ? (
                        <BoutiqueCategoryIcon className="h-6 w-6 text-amber-300" />
                      ) : (
                        category.icon || "🍽️"
                      )}
                    </div>
                    <div>
                    <p
                      className={`text-sm uppercase tracking-[0.3em] ${subtleAccentText}`}
                    >
                      {(category.products_count ?? 0)} article(s)
                    </p>
                      <h3 className="text-xl font-semibold">{category.name}</h3>
                    </div>
                  </div>
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10"
                    style={{ backgroundColor: `${category.color || "#fbbf24"}30` }}
                  >
                    <span
                      className="h-4 w-4 rounded-full border border-white/40"
                      style={{
                        backgroundColor: category.color || "#fbbf24",
                        opacity: category.is_active ? 1 : 0.35,
                      }}
                    />
                  </span>
                </div>
                <p className={`text-sm ${textMuted}`}>
                  {category.description || "Aucune description fournie."}
                </p>
                <div
                  className={`flex justify-between items-center pt-3 border-t ${dividerClasses}`}
                >
                  <p className={`text-xs ${subtleAccentText}`}>
                    {category.is_active ? "Visible sur la caisse" : "Masquée sur la caisse"}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleActive(category)}
                      className={`rounded-full border border-white/10 p-2 ${
                        category.is_active
                          ? "text-emerald-400 hover:border-emerald-400/60"
                          : "text-amber-300 hover:border-amber-300/60"
                      }`}
                      title={category.is_active ? "Désactiver" : "Activer"}
                    >
                      <Power size={16} />
                    </button>
                    <button
                      onClick={() => setEditingCategory(category)}
                      className="rounded-full border border-white/10 p-2 hover:border-amber-400/60"
                      title="Modifier"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

const ExtraCategoriesTab = ({ isDarkMode, cardClasses }) => {
  const [extraCategories, setExtraCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  const fetchExtraCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/extra-categories`);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur ${response.status}: ${errorText}`);
      }
      const data = await response.json();
      setExtraCategories(data);
    } catch (err) {
      console.error("Erreur lors du chargement des catégories d'extras:", err);
      setError(
        `Erreur lors du chargement des catégories d'extras: ${err.message}`,
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExtraCategories();
  }, [fetchExtraCategories]);

  const handleSaveCategory = async (category) => {
    const url = category.id
      ? `${API_BASE}/extra-categories/${category.id}`
      : `${API_BASE}/extra-categories`;
    const method = category.id ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(category),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Échec de la sauvegarde");
      }
      await fetchExtraCategories();
      setEditingCategory(null);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde:", err);
      alert(`Erreur lors de la sauvegarde: ${err.message}`);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir supprimer cette catégorie d'extras ?",
      )
    ) {
      try {
        const response = await fetch(
          `${API_BASE}/extra-categories/${categoryId}`,
          { method: "DELETE" },
        );
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || "Échec de la suppression");
        }
        await fetchExtraCategories();
      } catch (err) {
        console.error("Erreur lors de la suppression:", err);
        alert(`Erreur lors de la suppression: ${err.message}`);
      }
    }
  };

  const handleAddNew = () => {
    setEditingCategory({ name: "", description: "" });
  };

  const textMuted = isDarkMode ? "text-white/60" : "text-slate-500";
  const subtleAccentText = isDarkMode ? "text-white/50" : "text-slate-400";
  const dividerClasses = isDarkMode ? "border-white/5" : "border-slate-100";

  return (
    <div className="space-y-4">
      {editingCategory && (
        <CategoryModal
          category={editingCategory}
          onSave={handleSaveCategory}
          onCancel={() => setEditingCategory(null)}
          isExtraCategory
          isDarkMode={isDarkMode}
        />
      )}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-emerald-400">
            Catégories extras
          </p>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold">Options additionnelles</h2>
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                isDarkMode
                  ? "bg-white/10 border border-white/15"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {extraCategories.length} actives
            </span>
          </div>
          <p className={`text-sm ${textMuted}`}>
            Organisez les suppléments, garnitures et choix optionnels.
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="rounded-2xl bg-gradient-to-r from-emerald-400 to-teal-500 px-5 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-emerald-500/30 flex items-center gap-2"
        >
          <PlusCircle size={18} />
          Ajouter une catégorie d'extras
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-400 border-b-transparent" />
        </div>
      ) : error ? (
        <div
          className={`${cardClasses} rounded-3xl border border-rose-500/30 p-6 ${
            isDarkMode ? "text-rose-200" : "text-rose-600"
          }`}
        >
          <p className="font-semibold">{error}</p>
          <button
            onClick={fetchExtraCategories}
            className={`mt-4 rounded-2xl px-4 py-2 text-sm font-semibold ${
              isDarkMode
                ? "bg-rose-500/80 text-white"
                : "bg-rose-500 text-white"
            }`}
          >
            Réessayer
          </button>
        </div>
      ) : extraCategories.length === 0 ? (
        <div className={`${cardClasses} rounded-3xl p-10 text-center`}>
          <p className="text-lg font-semibold">Aucune catégorie d'extras</p>
          <p className={`text-sm mt-2 ${textMuted}`}>
            Définissez vos options additionnelles pour enrichir les commandes.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {extraCategories.map((category) => (
            <div
              key={category.id}
              className={`${cardClasses} rounded-3xl p-5 space-y-4`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">
                    Extras
                  </p>
                  <h3 className="text-xl font-semibold">{category.name}</h3>
                </div>
              </div>
              <p className={`text-sm ${textMuted}`}>
                {category.description || "Aucune description fournie."}
              </p>
              <div
                className={`flex justify-between items-center pt-3 border-t ${dividerClasses}`}
              >
                <p className={`text-xs ${subtleAccentText}`}>
                  Liées aux fiches produits
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingCategory(category)}
                    className="rounded-full border border-white/10 p-2 hover:border-emerald-400/60"
                    title="Modifier"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryManagement = () => {
  const [activeTab, setActiveTab] = useState("menu");
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () =>
      setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          setBusinessType(extractBusinessType(data));
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
          }
          return;
        }
      } catch (_error) {
        // ignore and fallback to cache
      }
      try {
        const cached = localStorage.getItem("restaurant-info");
        if (cached) {
          const parsed = JSON.parse(cached);
          setBusinessType(extractBusinessType(parsed));
        }
      } catch (_error) {
        // ignore
      }
    };
    loadBusinessType();
  }, []);

  useEffect(() => {
    if (isBoutique && activeTab === "extras") {
      setActiveTab("menu");
    }
  }, [isBoutique, activeTab]);

  const tabs = isBoutique
    ? [{ id: "menu", label: "Catégories produits", icon: Utensils }]
    : [
        { id: "menu", label: "Catégories menu", icon: Utensils },
        { id: "extras", label: "Catégories extras", icon: Package },
      ];

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#050b18] via-[#0b1428] to-[#050b18] text-slate-100"
    : "bg-slate-50 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-100 shadow-xl shadow-slate-200/70";

  return (
    <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
      <div className="mx-auto max-w-7xl space-y-6">
        <header className={`${cardClasses} rounded-3xl p-6 space-y-6`}>
          <div className="flex flex-col gap-4">
            <p className="text-xs uppercase tracking-[0.4em] text-amber-400">
              Architecture du menu
            </p>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-semibold tracking-tight">
                  {isBoutique ? "Catégories" : "Catégories & extras"}
                </h1>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-white/60" : "text-slate-500"
                  }`}
                >
                  {isBoutique
                    ? "Organisez vos familles de produits pour la caisse."
                    : "Harmonisez la navigation du menu, côté caisse et côté client."}
                </p>
              </div>
              {!isBoutique && (
                <div className="flex gap-3 bg-white/5 border border-white/10 rounded-2xl p-1">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                          isActive
                            ? "bg-amber-400 text-slate-900 shadow-lg"
                            : isDarkMode
                              ? "text-slate-300"
                              : "text-slate-500"
                        }`}
                      >
                        <Icon size={16} />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </header>

        <section className={`${cardClasses} rounded-3xl p-6`}>
          {activeTab === "menu" ? (
            <MenuCategoriesTab
              isDarkMode={isDarkMode}
              cardClasses={cardClasses}
              isBoutique={isBoutique}
            />
          ) : (
            <ExtraCategoriesTab
              isDarkMode={isDarkMode}
              cardClasses={cardClasses}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default CategoryManagement;
